import { keyed } from "lit/directives/keyed.js";
import "../components/cpl-code-editor";
import "../components/cpl-markdown-editor";
import "../components/cpl-markdown-viewer";
import {
  EXECUTION_TIMEOUT_SECONDS,
  executionTimeoutVariables,
} from "../execution-limits";
import { TableColumns } from "../components/table-columns";
import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  api,
  type JsonObject,
  type TaskRecord,
  type CodexConfigSource,
  type AuditEvent,
} from "../api/client";
import { i18n } from "../i18n";
import { dateTime, readable } from "../view-model";
import "../components/app-dialog";
import "../components/run-history-export";
import { clampPage, pageCount, pageItems } from "../components/app-pagination";
import {
  nextTableSort,
  tableSortQuery,
  sortableTableHeader,
  sortTableItems,
  type TableSort,
} from "../components/table-sort";
import { waitForLoadingCycle } from "../components/table-loading";
import "../components/a2k-skill-multiselect";
import type { A2kSkillOption } from "../components/a2k-skill-multiselect";

import {
  configPayload,
  configSourceField,
  configSourceLabel,
} from "../components/codex-config-source";

type JobEditorMode = "create" | "edit" | "delete" | null;

@customElement("jobs-view")
export class JobsView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private tasks: TaskRecord[] = [];
  @state() private page = 1;
  @state() private historyTaskId = "";
  @state() private historyRevision = 0;
  @state() private mode: JobEditorMode = null;
  private editorSession = 0;
  @state() private target: TaskRecord | null = null;
  @state() private name = "";
  @state() private description = "";
  @state() private taskDetail: TaskRecord | null = null;
  @state() private taskType:
    "bash" | "cmd" | "python" | "javascript" | "codex" = "bash";
  @state() private script = "";
  @state() private startAt = "";
  private initialStartAt = "";
  @state() private runningTasks = new Set<string>();
  @state() private intervalSeconds = 3600;
  @state() private scheduleMode: "interval" | "cron" = "interval";
  @state() private cronExpression = "";
  @state() private timezone = "Asia/Shanghai";
  @state() private timeout = EXECUTION_TIMEOUT_SECONDS;
  @state() private configSource: CodexConfigSource = "custom";
  @state() private providerBaseUrl = "";
  @state() private providerApiKey = "";
  @state() private model = "gpt-5.5";
  @state() private effort:
    "" | "low" | "medium" | "high" | "xhigh" | "max" | "ultra" = "medium";
  @state() private prompt = "";
  @state() private systemPrompt = "";
  @state() private skills: A2kSkillOption[] = [];
  @state() private selectedSkills: string[] = [];
  @state() private enabled = true;
  @state() private clearEnvironment = false;
  @state()
  private environment: Array<{
    key: string;
    value: string;
    visible: boolean;
  }> = [];
  @state() private loading = true;
  @state() private busy = false;
  @state() private error = "";
  @state() private dialogError = "";
  @state() private success = "";
  @state() private sort: TableSort = { key: "name", direction: "asc" };

  private readonly languageChanged = () => this.requestUpdate();

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
    void api
      .skills()
      .then((result) => {
        this.skills = result.items;
      })
      .catch(() => undefined);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private async load() {
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    try {
      this.tasks = (await api.tasks()).items;
      this.page = clampPage(this.page, this.tasks.length);
      this.historyRevision += 1;
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      await waitForLoadingCycle(loadingStarted);
      this.loading = false;
    }
  }

  private openCreate() {
    this.editorSession += 1;
    this.mode = "create";
    this.target = null;
    this.name = "";
    this.description = "";
    this.taskType = "bash";
    this.script = "";
    this.startAt = "";
    this.initialStartAt = "";
    this.intervalSeconds = 3600;
    this.scheduleMode = "interval";
    this.cronExpression = "";
    this.timezone = "Asia/Shanghai";
    this.timeout = EXECUTION_TIMEOUT_SECONDS;
    this.configSource = "custom";
    this.providerBaseUrl = "";
    this.providerApiKey = "";
    this.model = "gpt-5.5";
    this.effort = "medium";
    this.prompt = "";
    this.systemPrompt = "";
    this.selectedSkills = [];
    this.enabled = true;
    this.environment = [];
    this.clearEnvironment = false;
    this.dialogError = "";
  }

  private openEdit(task: TaskRecord) {
    this.editorSession += 1;
    this.mode = "edit";
    this.target = task;
    this.name = task.name;
    this.description = task.description || "";

    // Determine task type from category and shell
    if (task.category === "codex") {
      this.taskType = "codex";
    } else {
      this.taskType = task.shell || "bash";
    }

    this.script = task.script || "";

    this.intervalSeconds = Number(task.interval_seconds || 3600);
    this.scheduleMode =
      task.schedule_mode ||
      (task.cron_expression || task.cron ? "cron" : "interval");
    this.cronExpression = String(task.cron_expression || task.cron || "");
    this.timezone = task.timezone || "Asia/Shanghai";
    if (task.start_at) {
      const date = new Date(task.start_at);
      this.startAt = Number.isNaN(date.getTime())
        ? ""
        : new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
            .toISOString()
            .slice(0, 16);
    } else {
      this.startAt = "";
    }

    this.initialStartAt = this.startAt;
    this.timeout = Number(
      task.timeout_seconds || task.timeout || EXECUTION_TIMEOUT_SECONDS,
    );
    this.providerBaseUrl = task.provider_base_url || "";
    this.providerApiKey = "";
    this.configSource = task.config_source || "custom";
    this.model = task.model || (this.configSource === "local" ? "" : "gpt-5.5");
    this.effort =
      task.effort || (this.configSource === "local" ? "" : "medium");
    this.prompt = task.prompt || "";
    this.systemPrompt = task.system_prompt || "";
    this.selectedSkills = [...(task.skills || [])];
    this.enabled = task.enabled;
    // Existing environment values are intentionally masked by the API.
    // An empty editor means preserve the stored environment on PATCH.
    this.clearEnvironment = false;
    this.environment = (task.env_keys || []).map((key) => ({
      key,
      value: "",
      visible: false,
    }));
    this.dialogError = "";
  }

  private changeConfigSource(value: CodexConfigSource) {
    this.configSource = value;
    this.model = value === "local" ? "" : "gpt-5.5";
    this.effort = value === "local" ? "" : "medium";
  }

  private closeDialog() {
    if (this.busy) return;
    this.mode = null;
    this.target = null;
    this.dialogError = "";
  }

  private environmentPlaceholder(key: string) {
    return i18n.t(
      this.target?.env_keys?.includes(key)
        ? "jobsEnvironmentKeep"
        : "jobsEnvironmentValue",
    );
  }

  private parseEnvironment(): JsonObject | null {
    const values: JsonObject = {};
    const keys = new Set<string>();
    for (const row of this.environment) {
      const key = row.key.trim();
      if (!key && !row.value) continue;
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
        this.dialogError = i18n.t("jobsEnvironmentKeyInvalid");
        return null;
      }
      if (keys.has(key)) {
        this.dialogError = i18n.t("jobsEnvironmentDuplicate");
        return null;
      }
      keys.add(key);
      if (
        !this.clearEnvironment &&
        this.target?.env_keys?.includes(key) &&
        row.value === ""
      )
        continue;
      values[key] = row.value;
    }
    return values;
  }

  private async saveJob() {
    const isCodex = this.taskType === "codex";

    if (
      !this.name.trim() ||
      (!isCodex && !this.script.trim()) ||
      (isCodex && !this.prompt.trim())
    ) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }

    if (
      (this.scheduleMode === "interval" &&
        (!Number.isInteger(this.intervalSeconds) ||
          this.intervalSeconds < 1)) ||
      (this.scheduleMode === "cron" &&
        (this.cronExpression.trim().split(/\s+/).length !== 5 ||
          !this.timezone.trim())) ||
      (this.startAt && Number.isNaN(new Date(this.startAt).getTime()))
    ) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }

    if (
      !Number.isInteger(this.timeout) ||
      this.timeout < 1 ||
      this.timeout > EXECUTION_TIMEOUT_SECONDS
    ) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }

    const environment = this.parseEnvironment();
    if (this.environment.length && !environment) return;

    const values: JsonObject = {
      name: this.name.trim(),
      description: this.description,
      category: isCodex ? "codex" : "script",
      shell: isCodex ? "bash" : this.taskType,
      script: this.script,
      interval_seconds: this.intervalSeconds,
      schedule_mode: this.scheduleMode,
      cron_expression: this.cronExpression.trim(),
      timezone: this.timezone.trim(),
      start_at:
        this.target && this.startAt === this.initialStartAt
          ? this.target.start_at || null
          : this.startAt
            ? new Date(this.startAt).toISOString()
            : null,
      timeout_seconds: this.timeout,
      enabled: this.enabled,
    };

    if (isCodex) {
      values.provider_base_url = this.providerBaseUrl.trim();
      if (this.providerApiKey) values.provider_api_key = this.providerApiKey;
      values.model = this.model.trim();
      values.effort = this.effort;
      values.prompt = this.prompt;
      values.system_prompt = this.systemPrompt;
      values.skills = [...this.selectedSkills];
      const configured = configPayload(this.configSource, values, true);
      for (const key of Object.keys(values)) delete values[key];
      Object.assign(values, configured);
    }
    if (
      environment &&
      (this.mode === "create" || Object.keys(environment).length)
    )
      values.env = environment;

    if (this.mode === "edit" && this.target) {
      if (this.clearEnvironment) values.clear_env = true;
      else {
        const remaining = new Set(
          this.environment.map((row) => row.key.trim()),
        );
        const removed = (this.target.env_keys || []).filter(
          (key) => !remaining.has(key),
        );
        if (removed.length) values.env_delete_keys = removed;
      }
    }

    this.busy = true;
    this.dialogError = "";
    try {
      if (this.mode === "create") {
        await api.createTask(values);
        this.success = i18n.t("jobsCreatedStatus");
      } else if (this.target) {
        await api.updateTask(this.target.id, values);

        this.success = i18n.t("jobsUpdatedStatus");
      }
      this.mode = null;
      this.target = null;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private addEnvironment = () => {
    this.environment = [
      ...this.environment,
      { key: "", value: "", visible: false },
    ];
  };

  private removeEnvironment(index: number) {
    this.environment = this.environment.filter(
      (_, rowIndex) => rowIndex !== index,
    );
  }

  private updateEnvironment(
    index: number,
    field: "key" | "value",
    value: string,
  ) {
    this.environment = this.environment.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    );
  }

  private toggleEnvironmentVisibility(index: number) {
    this.environment = this.environment.map((row, rowIndex) =>
      rowIndex === index ? { ...row, visible: !row.visible } : row,
    );
  }

  protected firstUpdated() {
    if (window.location.hash === "#task-history")
      this.querySelector("#task-history")?.scrollIntoView();
  }

  private isRunning(task: TaskRecord) {
    return (
      this.runningTasks.has(task.id) ||
      ["running", "queued"].includes(task.last_status || "")
    );
  }

  private async run(id: string) {
    if (this.runningTasks.has(id)) return;
    this.runningTasks = new Set([...this.runningTasks, id]);
    this.error = "";
    try {
      await api.runTask(id);

      this.success = i18n.t("jobsQueuedStatus");
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.runningTasks = new Set(
        [...this.runningTasks].filter((value) => value !== id),
      );
    }
  }

  private async deleteTask() {
    if (!this.target) return;
    this.busy = true;
    this.dialogError = "";
    try {
      await api.deleteTask(this.target.id);
      this.success = i18n.t("jobsDeletedStatus");
      this.mode = null;
      this.target = null;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private get totalPages() {
    return pageCount(this.tasks.length);
  }

  private get visibleTasks() {
    return pageItems(
      sortTableItems(this.tasks, this.sort, (item, key) => item[key]),
      this.page,
    );
  }

  private setSort(key: string) {
    this.sort = nextTableSort(this.sort, key);
    this.page = 1;
  }

  private sortHeader(label: string, key: string) {
    return sortableTableHeader(label, key, this.sort, (value) =>
      this.setSort(value),
    );
  }

  private scheduleSummary(task: TaskRecord) {
    return task.schedule_mode === "cron" ||
      (!task.schedule_mode && (task.cron_expression || task.cron))
      ? `${task.cron_expression || task.cron} (${task.timezone || "Asia/Shanghai"})`
      : `${task.interval_seconds}s`;
  }

  private renderTaskDetails(task: TaskRecord) {
    const codex = task.category === "codex";
    return html`<dl class="detail-list task-detail-list">
      <dt>${i18n.t("name")}</dt>
      <dd>${task.name}</dd>
      <dt>${i18n.t("jobsDescriptionMarkdown")}</dt>
      <dd>
        <cpl-markdown-viewer
          .value=${task.description || ""}
        ></cpl-markdown-viewer>
      </dd>
      <dt>${i18n.t("jobsTaskType")}</dt>
      <dd>${codex ? i18n.t("jobsTaskTypeCodex") : task.shell}</dd>
      <dt>${i18n.t("jobsStartAt")}</dt>
      <dd>${dateTime(task.start_at)}</dd>
      <dt>${i18n.t("jobsScheduleMode")}</dt>
      <dd>${this.scheduleSummary(task)}</dd>
      <dt>${i18n.t("sourceNext")}</dt>
      <dd>${dateTime(task.next_run)}</dd>
      <dt>${i18n.t("jobsTimeout", executionTimeoutVariables)}</dt>
      <dd>${task.timeout_seconds}s</dd>
      <dt>${i18n.t("state")}</dt>
      <dd>${i18n.t(task.enabled ? "enabled" : "disabled")}</dd>
      <dt>${i18n.t("jobsEnvironment")}</dt>
      <dd>${readable(task.env_keys)}</dd>
      ${
        codex
          ? html`
              <dt>${i18n.t("codexConfigSource")}</dt>
              <dd>${configSourceLabel(task.config_source)}</dd>
              <dt>${i18n.t("jobsProviderBaseUrl")}</dt>
              <dd>${readable(task.provider_base_url)}</dd>
              <dt>${i18n.t("codexModel")}</dt>
              <dd>${task.model || i18n.t("codexConfigInherit")}</dd>
              <dt>${i18n.t("codexEffort")}</dt>
              <dd>${task.effort || i18n.t("codexConfigInherit")}</dd>
              <dt>${i18n.t("jobsSkills")}</dt>
              <dd>${readable(task.skills)}</dd>
              <dt>${i18n.t("jobsSystemPrompt")}</dt>
              <dd>
                <cpl-code-editor
                  .value=${task.system_prompt || ""}
                  .language=${"markdown"}
                  .readOnly=${true}
                  .label=${i18n.t("jobsSystemPrompt")}
                  .minHeight=${"10rem"}
                ></cpl-code-editor>
              </dd>
              <dt>${i18n.t("jobsPrompt")}</dt>
              <dd>
                <cpl-code-editor
                  .value=${task.prompt || ""}
                  .language=${"markdown"}
                  .readOnly=${true}
                  .label=${i18n.t("jobsPrompt")}
                  .minHeight=${"10rem"}
                ></cpl-code-editor>
              </dd>
            `
          : html`<dt>${i18n.t("jobsScript")}</dt>
              <dd>
                <cpl-code-editor
                  .value=${task.script || ""}
                  .language=${task.shell === "cmd" ? "text" : task.shell}
                  .readOnly=${true}
                  .label=${i18n.t("jobsScript")}
                  .minHeight=${"10rem"}
                ></cpl-code-editor>
              </dd>`
      }
    </dl>`;
  }

  render() {
    const deleting = this.mode === "delete";
    return html`<page-heading
        heading=${i18n.t("jobsTitle")}
        description=${i18n.t("jobsDescription")}
      >
        <a2k-button @click=${this.openCreate}>
          ${i18n.t("jobsCreate")}
        </a2k-button>
      </page-heading>
      ${
        this.error
          ? html`<p class="status status-error">${this.error}</p>`
          : null
      }
      ${
        this.success
          ? html`<p class="status status-success">${this.success}</p>`
          : null
      }
      <surface-window heading=${i18n.t("jobsInventory")}>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                ${this.sortHeader(i18n.t("name"), "name")}
                <th>${i18n.t("jobsTaskType")}</th>
                ${this.sortHeader(i18n.t("jobsScheduleMode"), "interval_seconds")}
                <th>${i18n.t("jobsEnvironment")}</th>
                ${this.sortHeader(i18n.t("state"), "last_status")}
                <th>${i18n.t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              ${
                this.loading
                  ? html`<tr>
                      <td colspan="6">
                        <table-loading
                          label=${i18n.t("loading")}
                        ></table-loading>
                      </td>
                    </tr>`
                  : this.visibleTasks.length
                    ? this.visibleTasks.map(
                        (task) =>
                          html`<tr>
                            <td>${task.name}</td>
                            <td>
                              ${
                                task.category === "codex"
                                  ? html`${configSourceLabel(task.config_source)}
                                    路
                                    ${task.model || i18n.t("codexConfigInherit")}`
                                  : html`<code>${task.shell}</code>`
                              }
                            </td>
                            <td>
                              ${this.scheduleSummary(task)}<br />${i18n.t("sourceNext")}:
                              ${dateTime(task.next_run)}
                            </td>
                            <td>${readable(task.env_keys)}</td>
                            <td>
                              ${
                                task.last_status ||
                                (task.enabled
                                  ? i18n.t("enabled")
                                  : i18n.t("disabled"))
                              }
                            </td>
                            <td>
                              <div class="resource-actions">
                                <a2k-button @click=${() => this.openEdit(task)}
                                  >${i18n.t("edit")}</a2k-button
                                >
                                <a2k-button
                                  ?disabled=${this.isRunning(task)}
                                  @click=${() => this.run(task.id)}
                                  >${i18n.t("run")}</a2k-button
                                >
                                <a2k-button
                                  @click=${() => {
                                    this.taskDetail = task;
                                  }}
                                  >${i18n.t("details")}</a2k-button
                                >
                                <a2k-button
                                  @click=${() => {
                                    this.target = task;
                                    this.mode = "delete";
                                    this.dialogError = "";
                                  }}
                                  >${i18n.t("remove")}</a2k-button
                                >
                              </div>
                            </td>
                          </tr>`,
                      )
                    : html`<tr>
                        <td class="empty-cell" colspan="6">
                          ${i18n.t("jobsEmpty")}
                        </td>
                      </tr>`
              }
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                }}
              ></app-pagination>
      </div>
      <app-dialog
        .open=${this.mode !== null}
        heading=${
          deleting
            ? i18n.t("remove")
            : this.mode === "edit"
              ? i18n.t("jobsEditTitle")
              : i18n.t("jobsCreateTitle")
        }
        confirm-label=${deleting ? i18n.t("remove") : i18n.t("save")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        .busy=${this.busy}
        .message=${this.dialogError}
        @dialog-cancel=${this.closeDialog}
        @dialog-confirm=${deleting ? this.deleteTask : this.saveJob}
      >
        ${
          deleting
            ? html`<a2k-panel>${i18n.t("jobsConfirmDelete")}</a2k-panel>`
            : keyed(
                this.editorSession,
                html`<div
                  class="form-grid dialog-content-container task-editor-fields"
                >
                  <label class="form-field">
                    <span class="field-label">${i18n.t("name")}</span>
                    <input
                      class="a2k-native-input"
                      .value=${this.name}
                      @input=${(event: Event) => {
                        this.name = (event.target as HTMLInputElement).value;
                      }}
                    />
                  </label>
                  <label class="form-field wide"
                    ><span class="field-label"
                      >${i18n.t("jobsDescriptionMarkdown")}</span
                    >
                    <cpl-markdown-editor
                      .value=${this.description}
                      .label=${i18n.t("jobsDescriptionMarkdown")}
                      .disabled=${this.busy}
                      .minHeight=${"8rem"}
                      @value-change=${(
                        event: CustomEvent<{ value: string }>,
                      ) => {
                        this.description = event.detail.value;
                      }}
                    ></cpl-markdown-editor>
                  </label>
                  <label class="form-field">
                    <span class="field-label">${i18n.t("jobsTaskType")}</span>
                    <select
                      class="a2k-native-input"
                      .value=${this.taskType}
                      @change=${(event: Event) => {
                        this.taskType = (event.target as HTMLSelectElement)
                          .value as typeof this.taskType;
                      }}
                    >
                      <option value="bash">
                        ${i18n.t("jobsTaskTypeBash")}
                      </option>
                      <option value="cmd">${i18n.t("jobsTaskTypeCmd")}</option>
                      <option value="python">
                        ${i18n.t("jobsTaskTypePython")}
                      </option>
                      <option value="javascript">
                        ${i18n.t("jobsTaskTypeJavascript")}
                      </option>
                      <option value="codex">
                        ${i18n.t("jobsTaskTypeCodex")}
                      </option>
                    </select>
                  </label>
                  ${
                    this.taskType === "codex"
                      ? html`
                          ${configSourceField(this.configSource, (value) => this.changeConfigSource(value))}
                          ${
                            this.configSource === "custom"
                              ? html`
                                  <label class="form-field wide"
                                    ><span class="field-label"
                                      >${i18n.t("jobsProviderBaseUrl")}</span
                                    >
                                    <input
                                      class="a2k-native-input"
                                      .value=${this.providerBaseUrl}
                                      @input=${(event: Event) => {
                                        this.providerBaseUrl = (
                                          event.target as HTMLInputElement
                                        ).value;
                                      }}
                                    />
                                  </label>
                                  <label class="form-field"
                                    ><span class="field-label"
                                      >${i18n.t("jobsProviderApiKey")}</span
                                    >
                                    <input
                                      class="a2k-native-input"
                                      type="password"
                                      placeholder=${this.target ? i18n.t("jobsProviderApiKeyKeep") : ""}
                                      .value=${this.providerApiKey}
                                      @input=${(event: Event) => {
                                        this.providerApiKey = (
                                          event.target as HTMLInputElement
                                        ).value;
                                      }}
                                    />
                                  </label>
                                `
                              : nothing
                          }
                          <label class="form-field"
                            ><span class="field-label"
                              >${i18n.t("jobsModel")}</span
                            >
                            <input
                              class="a2k-native-input"
                              placeholder=${this.configSource === "local" ? i18n.t("codexConfigInherit") : ""}
                              .value=${this.model}
                              @input=${(event: Event) => {
                                this.model = (
                                  event.target as HTMLInputElement
                                ).value;
                              }}
                            />
                          </label>
                          <label class="form-field"
                            ><span class="field-label"
                              >${i18n.t("jobsEffort")}</span
                            >
                            <select
                              class="a2k-native-input"
                              .value=${this.effort}
                              @change=${(event: Event) => {
                                this.effort = (
                                  event.target as HTMLSelectElement
                                ).value as typeof this.effort;
                              }}
                            >
                              ${
                                this.configSource === "local"
                                  ? html`<option value="">
                                      ${i18n.t("codexConfigInherit")}
                                    </option>`
                                  : nothing
                              }
                              ${[
                                "low",
                                "medium",
                                "high",
                                "xhigh",
                                "max",
                                "ultra",
                              ].map(
                                (value) =>
                                  html`<option value=${value}>
                                    ${value}
                                  </option>`,
                              )}
                            </select>
                          </label>
                          <div class="form-field wide">
                            <span class="field-label"
                              >${i18n.t("jobsSkills")}</span
                            >
                            <a2k-skill-multiselect
                              .skills=${this.skills.filter((skill) => skill.enabled)}
                              .selected=${this.selectedSkills}
                              @selection-change=${(
                                event: CustomEvent<string[]>,
                              ) => {
                                this.selectedSkills = event.detail;
                              }}
                            ></a2k-skill-multiselect>
                          </div>
                          <label class="form-field wide"
                            ><span class="field-label"
                              >${i18n.t("jobsSystemPrompt")}</span
                            >
                            <cpl-code-editor
                              .value=${this.systemPrompt}
                              language="markdown"
                              .label=${i18n.t("jobsSystemPrompt")}
                              .disabled=${this.busy}
                              .minHeight=${"8rem"}
                              @value-change=${(
                                event: CustomEvent<{ value: string }>,
                              ) => {
                                this.systemPrompt = event.detail.value;
                              }}
                            ></cpl-code-editor>
                          </label>
                          <label class="form-field wide"
                            ><span class="field-label"
                              >${i18n.t("jobsPrompt")}</span
                            >
                            <cpl-code-editor
                              .value=${this.prompt}
                              language="markdown"
                              .label=${i18n.t("jobsPrompt")}
                              .disabled=${this.busy}
                              .minHeight=${"14rem"}
                              @value-change=${(
                                event: CustomEvent<{ value: string }>,
                              ) => {
                                this.prompt = event.detail.value;
                              }}
                            ></cpl-code-editor>
                          </label>
                        `
                      : html`<label class="form-field wide">
                          <span class="field-label"
                            >${i18n.t("jobsScript")}</span
                          >
                          <cpl-code-editor
                            class="task-script-input"
                            .value=${this.script}
                            .language=${this.taskType === "cmd" ? "text" : this.taskType}
                            .label=${i18n.t("jobsScript")}
                            .disabled=${this.busy}
                            .minHeight=${"14rem"}
                            @value-change=${(
                              event: CustomEvent<{ value: string }>,
                            ) => {
                              this.script = event.detail.value;
                            }}
                          ></cpl-code-editor>
                        </label>`
                  }
                  <label class="form-field">
                    <span class="field-label">${i18n.t("jobsStartAt")}</span>
                    <input
                      class="a2k-native-input"
                      type="datetime-local"
                      .value=${this.startAt}
                      @input=${(event: Event) => {
                        this.startAt = (event.target as HTMLInputElement).value;
                      }}
                    />
                    <small>${i18n.t("jobsStartAtHelp")}</small>
                  </label>
                  <label class="form-field">
                    <span class="field-label"
                      >${i18n.t("jobsScheduleMode")}</span
                    >
                    <select
                      class="a2k-native-input"
                      .value=${this.scheduleMode}
                      @change=${(event: Event) => {
                        this.scheduleMode = (event.target as HTMLSelectElement)
                          .value as "interval" | "cron";
                      }}
                    >
                      <option value="interval">
                        ${i18n.t("jobsScheduleModeInterval")}
                      </option>
                      <option value="cron">
                        ${i18n.t("jobsScheduleModeCron")}
                      </option>
                    </select>
                  </label>
                  ${
                    this.scheduleMode === "cron"
                      ? html`
                          <label class="form-field">
                            <span class="field-label"
                              >${i18n.t("jobsCronExpression")}</span
                            >
                            <input
                              class="a2k-native-input"
                              placeholder="0 * * * *"
                              .value=${this.cronExpression}
                              @input=${(event: Event) => {
                                this.cronExpression = (
                                  event.target as HTMLInputElement
                                ).value;
                              }}
                            />
                            <small>${i18n.t("jobsCronHelp")}</small>
                          </label>
                          <label class="form-field">
                            <span class="field-label"
                              >${i18n.t("jobsTimezone")}</span
                            >
                            <input
                              class="a2k-native-input"
                              placeholder="Asia/Shanghai"
                              .value=${this.timezone}
                              @input=${(event: Event) => {
                                this.timezone = (
                                  event.target as HTMLInputElement
                                ).value;
                              }}
                            />
                          </label>
                        `
                      : html`
                          <label class="form-field">
                            <span class="field-label"
                              >${i18n.t("jobsInterval")}</span
                            >
                            <input
                              class="a2k-native-input"
                              type="number"
                              min="1"
                              step="1"
                              .value=${String(this.intervalSeconds)}
                              @input=${(event: Event) => {
                                this.intervalSeconds = Number(
                                  (event.target as HTMLInputElement).value,
                                );
                              }}
                            />
                          </label>
                        `
                  }
                  ${html`
                    <label class="form-field">
                      <span class="field-label"
                        >${i18n.t("jobsTimeout", executionTimeoutVariables)}</span
                      >
                      <input
                        class="a2k-native-input"
                        type="number"
                        min="1"
                        max=${EXECUTION_TIMEOUT_SECONDS}
                        step="1"
                        .value=${String(this.timeout)}
                        @input=${(event: Event) => {
                          this.timeout = Number(
                            (event.target as HTMLInputElement).value,
                          );
                        }}
                      />
                    </label>
                  `}
                  <label class="checkbox-field">
                    <input
                      type="checkbox"
                      .checked=${this.enabled}
                      @change=${(event: Event) => {
                        this.enabled = (
                          event.target as HTMLInputElement
                        ).checked;
                      }}
                    />
                    <span>${i18n.t("jobsEnabled")}</span>
                  </label>
                  ${html`<div class="wide">
                    <fieldset class="env-editor">
                      <legend>${i18n.t("jobsEnvironment")}</legend>
                      ${this.environment.map(
                        (row, index) =>
                          html`<div class="env-row">
                            <input
                              class="a2k-native-input"
                              type="text"
                              placeholder="${i18n.t("jobsEnvironmentKey")}"
                              .value=${row.key}
                              @input=${(event: Event) =>
                                this.updateEnvironment(
                                  index,
                                  "key",
                                  (event.target as HTMLInputElement).value,
                                )}
                            />
                            <input
                              class="a2k-native-input"
                              type=${row.visible ? "text" : "password"}
                              placeholder=${this.environmentPlaceholder(row.key)}
                              .value=${row.value}
                              @input=${(event: Event) =>
                                this.updateEnvironment(
                                  index,
                                  "value",
                                  (event.target as HTMLInputElement).value,
                                )}
                            />
                            <a2k-button
                              type="button"
                              aria-label=${
                                row.visible
                                  ? i18n.t("jobsHideEnvironmentValue")
                                  : i18n.t("jobsShowEnvironmentValue")
                              }
                              @click=${() => this.toggleEnvironmentVisibility(index)}
                              >${
                                row.visible
                                  ? i18n.t("jobsHideEnvironmentValue")
                                  : i18n.t("jobsShowEnvironmentValue")
                              }</a2k-button
                            >
                            <a2k-button
                              type="button"
                              @click=${() => this.removeEnvironment(index)}
                              >${i18n.t("remove")}</a2k-button
                            >
                          </div>`,
                      )}
                      <a2k-button type="button" @click=${this.addEnvironment}
                        >${i18n.t("jobsAddEnvironment")}</a2k-button
                      >
                      ${
                        this.target
                          ? html`<a2k-button
                              type="button"
                              @click=${() => {
                                this.environment = [];
                                this.clearEnvironment = true;
                              }}
                              >${i18n.t("jobsEnvironmentClear")}</a2k-button
                            >`
                          : nothing
                      }
                    </fieldset>
                  </div>`}
                </div>`,
              )
        }
      </app-dialog>
      <app-dialog .open=${Boolean(this.taskDetail)} heading=${i18n.t("details")} hide-confirm
        cancel-label=${i18n.t("close")} @dialog-cancel=${() => {
          this.taskDetail = null;
        }}>
        ${this.taskDetail ? this.renderTaskDetails(this.taskDetail) : nothing}
      </app-dialog>
      <section id="task-history" aria-label=${i18n.t("historyTitle")}>
        <history-view .embedded=${true} .availableTasks=${this.tasks} .selectedTaskId=${this.historyTaskId}
          .revision=${this.historyRevision}
          @task-selection=${(event: CustomEvent<string>) => {
            this.historyTaskId = event.detail;
          }}></history-view>
      </section>`;
  }
}

@customElement("history-view")
export class HistoryView extends LitElement {
  @property({ type: Boolean }) embedded = false;
  @property({ attribute: false }) availableTasks: TaskRecord[] = [];
  @property({ attribute: "selected-task-id" }) selectedTaskId = "";
  @property({ type: Number }) revision = 0;
  private historyRequest = 0;

  protected updated(changed: PropertyValues) {
    if (
      this.embedded &&
      (changed.has("availableTasks") ||
        changed.has("selectedTaskId") ||
        changed.has("revision"))
    ) {
      this.tasks = this.availableTasks;
      this.selectAvailableTask(this.selectedTaskId);
      void this.load();
    }
  }

  private selectAvailableTask(preferred = this.taskId) {
    const selected = preferred;
    const next = this.tasks.some((task) => task.id === selected)
      ? selected
      : "";
    if (next !== this.taskId) this.page = 1;
    this.taskId = next;
  }

  private readonly columns = new TableColumns(this);
  @state() private tasks: TaskRecord[] = [];
  @state() private taskId = "";
  @state() private page = 1;
  @state() private pageSize = 10;
  @state() private totalPages = 0;
  @state() private runs: JsonObject[] = [];
  @state() private detail: JsonObject | null = null;
  @state() private loading = true;
  @state() private error = "";
  @state() private sort: TableSort = { key: "started_at", direction: "desc" };

  private readonly languageChanged = () => this.requestUpdate();

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    if (!this.embedded) void this.initialize();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private async initialize() {
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    try {
      this.tasks = (await api.tasks()).items;
      this.selectAvailableTask(this.selectedTaskId);
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      await waitForLoadingCycle(loadingStarted);
      this.loading = false;
    }
  }

  private async load() {
    const request = ++this.historyRequest;
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    this.detail = null;
    try {
      const result = await api.runs({
        task_id: this.taskId || undefined,
        page: this.page,
        page_size: this.pageSize,
        ...tableSortQuery(this.sort),
      });
      if (request === this.historyRequest) {
        this.runs = result.items;
        this.totalPages = result.total_pages;
        this.page = result.page;
      }
    } catch (error) {
      if (request === this.historyRequest)
        this.error = (error as Error).message;
    } finally {
      await waitForLoadingCycle(loadingStarted);
      if (request === this.historyRequest) this.loading = false;
    }
  }

  private closeRunDetail() {
    this.detail = null;
  }

  private async inspect(id: unknown) {
    this.error = "";
    try {
      const task = this.taskId;
      const detail = (await api.run(String(id))).run;
      if (this.taskId === task) this.detail = detail;
    } catch (error) {
      this.error = (error as Error).message;
    }
  }

  private async deleteRun(id: unknown) {
    try {
      await api.deleteRun(String(id));
      this.detail = null;
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    }
  }

  private setSort(key: string) {
    this.sort = nextTableSort(this.sort, key);
    this.page = 1;
    void this.load();
  }

  private sortHeader(label: string, key: string) {
    return sortableTableHeader(label, key, this.sort, (value) =>
      this.setSort(value),
    );
  }

  private detailType() {
    return readable(this.detail?.shell || this.detail?.category);
  }

  render() {
    return html`${
      this.embedded
        ? nothing
        : html`<page-heading
            heading=${i18n.t("historyTitle")}
            description=${i18n.t("historyDescription")}
          ></page-heading>`
    }
    ${
      this.error ? html`<p class="status status-error">${this.error}</p>` : null
    }
    ${
      this.loading
        ? html`<table-loading label=${i18n.t("loading")}></table-loading>`
        : html`<div class="task-history-content">
            <surface-window heading=${i18n.t("historyRuns")}>
              <run-history-export
                kind="tasks"
                .filters=${{ task_id: this.taskId || undefined, ...tableSortQuery(this.sort) }}
              ></run-history-export>
              <div class="toolbar source-history-toolbar">
                <label class="native-field codex-history-page-size">
                  <span>${i18n.t("historyTask")}</span>
                  <select
                    class="a2k-native-input"
                    .value=${this.taskId}
                    ?disabled=${this.loading}
                    @change=${(event: Event) => {
                      this.taskId = (event.target as HTMLSelectElement).value;
                      this.page = 1;
                      this.dispatchEvent(
                        new CustomEvent("task-selection", {
                          detail: this.taskId,
                          bubbles: true,
                        }),
                      );
                      if (!this.embedded) void this.load();
                    }}
                  >
                    <option value="">${i18n.t("historyAllTasks")}</option>
                    ${this.tasks.map(
                      (task) =>
                        html`<option value=${task.id}>${task.name}</option>`,
                    )}
                  </select>
                </label>
                <label class="native-field codex-history-page-size">
                  <span>${i18n.t("codexHistoryPageSize")}</span>
                  <select
                    class="a2k-native-input"
                    .value=${String(this.pageSize)}
                    @change=${(event: Event) => {
                      this.pageSize = Number(
                        (event.target as HTMLSelectElement).value,
                      );
                      this.page = 1;
                      void this.load();
                    }}
                  >
                    ${[10, 20, 50, 100].map((size) => html`<option value=${size}>${size}</option>`)}
                  </select>
                </label>
                <a2k-button ?disabled=${this.loading} @click=${this.load}>
                  ${i18n.t("refresh")}
                </a2k-button>
              </div>
              <div class="table-wrap">
                <table class="data-table">
                  <thead>
                    <tr>
                      ${this.sortHeader(i18n.t("historyRun"), "id")}
                      <th>${i18n.t("historyTask")}</th>
                      ${this.sortHeader(i18n.t("historyStatus"), "status")}
                      ${this.sortHeader(i18n.t("historyStarted"), "started_at")}
                      ${this.sortHeader(i18n.t("historyFinished"), "finished_at")}
                      <th>${i18n.t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      this.runs.length
                        ? this.runs.map(
                            (run) =>
                              html`<tr>
                                <td>${readable(run.id)}</td>
                                <td>
                                  ${readable(run.task_name || run.task_id)}
                                </td>
                                <td>${readable(run.status)}</td>
                                <td>${dateTime(run.started_at)}</td>
                                <td>${dateTime(run.finished_at)}</td>
                                <td>
                                  <a2k-button
                                    @click=${() => this.inspect(run.id)}
                                  >
                                    ${i18n.t("details")}
                                  </a2k-button>
                                  <a2k-button
                                    @click=${() => void this.deleteRun(run.id)}
                                  >
                                    ${i18n.t("remove")}
                                  </a2k-button>
                                </td>
                              </tr>`,
                          )
                        : html`<tr>
                            <td class="empty-cell" colspan="6">
                              ${i18n.t("historyEmpty")}
                            </td>
                          </tr>`
                    }
                  </tbody>
                </table>
              </div>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                  void this.load();
                }}
              ></app-pagination>
            </surface-window>
            ${
              this.detail
                ? html`<app-dialog
                    .open=${true}
                    heading=${i18n.t("historyRunDetail")}
                    hide-confirm
                    cancel-label=${i18n.t("close")}
                    @dialog-cancel=${this.closeRunDetail}
                  >
                    <dl class="detail-list">
                      ${
                        this.detail.category === "codex"
                          ? html`
                              <dt>${i18n.t("codexConfigSource")}</dt>
                              <dd>
                                ${configSourceLabel(this.detail.config_source as CodexConfigSource)}
                              </dd>
                              <dt>${i18n.t("codexModel")}</dt>
                              <dd>
                                ${this.detail.model || i18n.t("codexConfigInherit")}
                              </dd>
                              <dt>${i18n.t("codexEffort")}</dt>
                              <dd>
                                ${this.detail.effort || i18n.t("codexConfigInherit")}
                              </dd>
                            `
                          : nothing
                      }
                      ${
                        this.detail.category
                          ? html`<dt>${i18n.t("jobsTaskType")}</dt>
                              <dd>${this.detailType()}</dd>`
                          : nothing
                      }
                      <dt>${i18n.t("historyStatus")}</dt>
                      <dd>${readable(this.detail.status)}</dd>
                      <dt>${i18n.t("historyStarted")}</dt>
                      <dd>${dateTime(this.detail.started_at)}</dd>
                      <dt>${i18n.t("historyFinished")}</dt>
                      <dd>${dateTime(this.detail.finished_at)}</dd>
                      <dt>${i18n.t("historyReturnCode")}</dt>
                      <dd>${readable(this.detail.returncode)}</dd>
                    </dl>
                    <section class="output-section">
                      <h3>${i18n.t("historyStdout")}</h3>
                      <pre class="code-output">
${readable(this.detail.stdout)}</pre>
                    </section>
                    <section class="output-section">
                      <h3>${i18n.t("historyStderr")}</h3>
                      <pre class="code-output">
${readable(this.detail.stderr)}</pre>
                    </section>
                  </app-dialog>`
                : null
            }
          </div>`
    } `;
  }
}

@customElement("logs-view")
export class LogsView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private items: AuditEvent[] = [];
  @state() private method = "";
  @state() private status = "";
  @state() private actor = "";
  @state() private resource = "";
  @state() private page = 1;
  @state() private totalPages = 0;
  @state() private detail: AuditEvent | null = null;
  @state() private detailOpen = false;
  @state() private detailLoading = false;
  @state() private detailError = "";
  @state() private from = "";
  @state() private to = "";
  @state() private loading = true;
  @state() private error = "";
  @state() private sort: TableSort = { key: "timestamp", direction: "desc" };

  private readonly languageChanged = () => this.requestUpdate();
  private detailRequest = 0;
  private detailTrigger: HTMLElement | null = null;
  private readonly closeDetailOnEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.detailOpen) this.closeDetail();
  };

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("keydown", this.closeDetailOnEscape);
    void this.initialize();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("keydown", this.closeDetailOnEscape);
    this.detailRequest += 1;
    super.disconnectedCallback();
  }

  private async initialize() {
    await this.load();
  }

  private async load() {
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    try {
      const data = await api.auditEvents({
        page: this.page,
        page_size: 10,
        method: this.method,
        status: this.status,
        actor_type: this.actor,
        resource: this.resource,
        from: this.from ? new Date(this.from).toISOString() : "",
        to: this.to ? new Date(this.to).toISOString() : "",
        ...tableSortQuery(this.sort),
      });
      if (data.total_pages > 0 && this.page > data.total_pages) {
        this.page = data.total_pages;
        await this.load();
        return;
      }
      this.items = data.items;
      this.totalPages = data.total_pages;
      if (!this.totalPages) this.page = 1;
    } catch (error) {
      this.error = (error as Error).message;
      this.items = [];
    } finally {
      await waitForLoadingCycle(loadingStarted);
      this.loading = false;
    }
  }

  private async inspect(item: AuditEvent, trigger: HTMLElement) {
    const request = ++this.detailRequest;
    this.detailTrigger = trigger;
    this.detailOpen = true;
    this.detailLoading = true;
    this.detailError = "";
    this.detail = null;
    try {
      const { event } = await api.auditEvent(item.id);
      if (request === this.detailRequest && this.detailOpen) {
        this.detail = event;
      }
    } catch (error) {
      if (request === this.detailRequest && this.detailOpen) {
        this.detailError = i18n.t("logsDetailLoadError", {
          message: (error as Error).message,
        });
      }
    } finally {
      if (request === this.detailRequest && this.detailOpen) {
        this.detailLoading = false;
      }
    }
  }

  private async clearLogs() {
    try {
      await api.clearAuditEvents();
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    }
  }

  private closeDetail() {
    if (!this.detailOpen) return;
    this.detailRequest += 1;
    this.detailOpen = false;
    this.detailLoading = false;
    this.detailError = "";
    this.detail = null;
    const trigger = this.detailTrigger;
    this.detailTrigger = null;
    void this.updateComplete.then(() => {
      trigger?.focus();
      trigger?.shadowRoot?.querySelector<HTMLElement>("button")?.focus();
    });
  }

  private setSort(key: string) {
    this.sort = nextTableSort(this.sort, key);
    this.page = 1;
    void this.load();
  }

  private sortHeader(label: string, key: string) {
    return sortableTableHeader(label, key, this.sort, (value) =>
      this.setSort(value),
    );
  }

  render() {
    return html`<page-heading
        heading=${i18n.t("logsTitle")}
        description=${i18n.t("logsDescription")}
      ></page-heading>
      <surface-window heading=${i18n.t("logsFilters")}>
        <div class="toolbar log-filters">
          <label class="native-field">
            ${i18n.t("logsMethod")}
            <select
              class="a2k-native-input"
              .value=${this.method}
              @change=${(event: Event) => {
                this.method = (event.target as HTMLSelectElement).value;
                this.page = 1;
              }}
            >
              <option value="">${i18n.t("logsAll")}</option>
              ${["GET", "POST", "PATCH", "DELETE"].map((item) => html`<option value=${item}>${item}</option>`)}
            </select>
          </label>
          <label class="native-field">
            ${i18n.t("logsStatus")}
            <select
              class="a2k-native-input"
              .value=${this.status}
              @change=${(event: Event) => {
                this.status = (event.target as HTMLSelectElement).value;
                this.page = 1;
              }}
            >
              <option value="">${i18n.t("logsAll")}</option>
              ${[200, 201, 400, 401, 403, 404, 500].map((item) => html`<option value=${item}>${item}</option>`)}
            </select>
          </label>
          <label class="native-field"
            >${i18n.t("logsActor")}<select
              class="a2k-native-input"
              .value=${this.actor}
              @change=${(event: Event) => {
                this.actor = (event.target as HTMLSelectElement).value;
                this.page = 1;
              }}
            >
              <option value="">${i18n.t("logsAll")}</option>
              <option value="anonymous">anonymous</option>
              <option value="session">session</option>
              <option value="api_key">api_key</option>
            </select></label
          >
          <label class="native-field"
            >${i18n.t("logsResource")}<input
              class="a2k-native-input"
              .value=${this.resource}
              @input=${(event: Event) => {
                this.resource = (event.target as HTMLInputElement).value;
                this.page = 1;
              }}
          /></label>
          <label class="native-field">
            ${i18n.t("logsFrom")}
            <input
              class="a2k-native-input"
              type="datetime-local"
              .value=${this.from}
              @change=${(event: Event) => {
                this.from = (event.target as HTMLInputElement).value;
                this.page = 1;
              }}
            />
          </label>
          <label class="native-field">
            ${i18n.t("logsTo")}
            <input
              class="a2k-native-input"
              type="datetime-local"
              .value=${this.to}
              @change=${(event: Event) => {
                this.to = (event.target as HTMLInputElement).value;
                this.page = 1;
              }}
            />
          </label>
          <a2k-button ?disabled=${this.loading} @click=${this.load}>
            ${i18n.t("refresh")}
          </a2k-button>
          <a2k-button ?disabled=${this.loading} @click=${this.clearLogs}>
            ${i18n.t("remove")}
          </a2k-button>
        </div>
      </surface-window>
      ${
        this.error
          ? html`<p class="status status-error">${this.error}</p>`
          : null
      }
      <surface-window heading=${i18n.t("logsLatest")}>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                ${this.sortHeader(i18n.t("logsTimestamp"), "timestamp")}
                ${this.sortHeader(i18n.t("logsMethod"), "method")}
                ${this.sortHeader(i18n.t("logsStatus"), "status_code")}
                ${this.sortHeader(i18n.t("logsResource"), "resource")}
                ${this.sortHeader(i18n.t("logsDuration"), "duration_ms")}
                <th>${i18n.t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              ${
                this.loading
                  ? html`<tr>
                      <td colspan="6">
                        <table-loading
                          label=${i18n.t("loading")}
                        ></table-loading>
                      </td>
                    </tr>`
                  : this.items.length
                    ? this.items.map(
                        (item) =>
                          html`<tr>
                            <td>${dateTime(item.timestamp)}</td>
                            <td>${item.method}</td>
                            <td>${item.status_code}</td>
                            <td>${item.resource}</td>
                            <td>${item.duration_ms}ms</td>
                            <td>
                              <a2k-button
                                @click=${(event: Event) =>
                                  this.inspect(
                                    item,
                                    event.currentTarget as HTMLElement,
                                  )}
                                >${i18n.t("details")}</a2k-button
                              >
                            </td>
                          </tr>`,
                      )
                    : html`<tr>
                        <td class="empty-cell" colspan="6">
                          ${i18n.t("logsEmpty")}
                        </td>
                      </tr>`
              }
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                  void this.load();
                }}
              ></app-pagination>
      </div>
      <app-dialog
                .open=${this.detailOpen}
                heading=${i18n.t("logsDetail")}
                cancel-label=${i18n.t("close")}
                .hideConfirm=${true}
                .message=${this.detailError}
                @dialog-cancel=${this.closeDetail}
              >
                <div class="logs-detail" aria-busy=${this.detailLoading}>
                  ${
                    this.detailLoading
                      ? html`<div class="logs-detail-loading">
                          <a2k-progress></a2k-progress>
                          <span>${i18n.t("logsDetailLoading")}</span>
                        </div>`
                      : this.detail
                        ? html`<dl class="detail-list logs-detail-list">
                            <dt>${i18n.t("logsRequestId")}</dt>
                            <dd>${this.detail.request_id}</dd>
                            <dt>${i18n.t("logsPath")}</dt>
                            <dd>${this.detail.path}</dd>
                            <dt>${i18n.t("logsActor")}</dt>
                            <dd>
                              ${this.detail.actor_type} ${this.detail.user_name}
                            </dd>
                            <dt>${i18n.t("logsFields")}</dt>
                            <dd>${readable(this.detail.request_fields)}</dd>
                            <dt>${i18n.t("logsRedaction")}</dt>
                            <dd>${readable(this.detail.redaction)}</dd>
                          </dl>`
                        : null
                  }
                </div>
              </app-dialog>
    `;
  }
}
